import moment from "moment-timezone";
import { isEqual, omit } from "lodash";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";

export const __MIN_SECTION_DURATION = 300;

// A section's end can be dragged/typed past the end of the sections that follow it, so
// the shift's own boundaries are the widest span its sections cover - taking
// sections[0].start / sections[last].end instead would clip the shift back to the last
// row's end and lose the edit. Numeric (seconds-offset) and empty values are skipped:
// call sites resolve those against their own start date before asking for bounds.
export const getSectionsBounds = (sections: any) => {
    let start: any;
    let end: any;

    (sections ?? []).forEach((section: any) => {
        const sectionStart = (section?.start && typeof section.start !== "number") ? moment(section.start) : undefined;
        const sectionEnd = (section?.end && typeof section.end !== "number") ? moment(section.end) : undefined;

        if (sectionStart?.isValid() && (!start || sectionStart.isBefore(start))) {
            start = sectionStart;
        }
        if (sectionEnd?.isValid() && (!end || sectionEnd.isAfter(end))) {
            end = sectionEnd;
        }
    });

    return { start: start?.toDate(), end: end?.toDate() };
};

export const getIntervals = (sections: any) => {
    return sections?.map((section: any) => {
        const duration = typeof section?.end === "number"
            ? section?.end - section?.start
            : moment(section?.end).diff(moment(section?.start), "seconds");

        return {
            type: section?.type,
            duration
        }
    })
};

export const getIntervalWidth = (intervals: any, sections: any, duration: number) => {
    const totalDuration = intervals.reduce(
        (total: any, interval: any) => interval.duration + total, 0
    );

    const lastSection = sections?.length === intervals?.length;

    if (lastSection) {
        return duration / totalDuration;
    }
    return duration / totalDuration / 2;
};

export const getAutomaticBreaks = (calcGroupBreaks: any, shifts: any, defaultSection: any) => {
    let start: any, end: any, seconds = 0;
    const startDate = moment(shifts?.[0]?.start || defaultSection?.start);
    const endDate = moment(shifts?.[shifts?.length - 1]?.end || defaultSection?.end);
    const diff = moment(endDate).diff(moment(startDate), "seconds");

    if (!!calcGroupBreaks?.automatic?.hideBreakAndDeductTotal) {
        return calcGroupBreaks?.automatic?.breaks
            ?.filter((item: any) => item?.after && item.amount)
            ?.map(({ after, amount }: any) => {
                if (diff >= (Number(after) + Number(amount)) * 60 + 900) {
                    const start = moment(startDate).add(5, "hours");
                    const end = moment(start).add(Number(amount), "minutes");

                    return {
                        ...defaultSection,
                        start,
                        end,
                        type: "break",
                        payCode: calcGroupBreaks?.automatic?.payCode,
                        duration: renderDurationAsFormat(Number(amount) * 60, "HH:mm"),
                        after: 18000
                    }
                }
            })?.filter((item: any) => item);
    } else {
        return calcGroupBreaks?.automatic?.breaks
            ?.filter((item: any) => item?.after && item.amount)
            ?.map(({ after, amount }: any, i: number) => {
                const minutes = Number(after) > 15 ? Number(after) : 15;
                if (i === 0) {
                    start = moment(startDate).add(minutes, "minutes");
                    end = moment(start).add(Number(amount), "minutes");
                } else if (i > 0) {
                    start = moment(end).add(after, "minutes");
                    end = moment(start).add(Number(amount), "minutes");
                }
                seconds += (Number(after || 0) + Number(amount || 0)) * 60;
                if (diff >= seconds + 900) {
                    return {
                        ...defaultSection,
                        start,
                        end,
                        after,
                        duration: renderDurationAsFormat(Number(amount) * 60, "HH:mm"),
                        type: "break",
                        payCode: calcGroupBreaks?.automatic?.payCode
                    }
                }
            })?.filter((item: any) => item);
    }
}


export const mergeSections = (sections: any) => {
    if (!sections?.length) return [];

    const newSections = [];
    let currentSection = sections[0];

    for (let i = 1; i < sections.length; i++) {
        const s = sections[i];

        if (areSectionsEqual(currentSection, s)) {
            currentSection.end = s.end;
            currentSection.duration = renderDurationAsFormat(
                moment(currentSection.end).diff(moment(currentSection.start), "seconds"),
                "HH:mm"
            );
        } else {
            newSections.push(currentSection);
            currentSection = s;
        }
    }

    newSections.push(currentSection);

    return newSections;
};

const getIds = (items: any) => (items || []).map((item: any) => item?.id ?? item).sort();

export const areSectionsEqual = (section1: any, section2: any) => {
    return section1.type === section2.type &&
        section1?.payCode?.code === section2?.payCode?.code &&
        section1?.location?.locationId === section2?.location?.locationId &&
        section1?.job?.jobId === section2?.job?.jobId &&
        section1?.job?.hourlyOrSalaried === section2?.job?.hourlyOrSalaried &&
        section1?.job?.hourlyRate === section2?.job?.hourlyRate &&
        section1?.level1?.id === section2?.level1?.id &&
        section1?.level2?.id === section2?.level2?.id &&
        section1?.level3?.id === section2?.level3?.id &&
        section1?.level4?.id === section2?.level4?.id &&
        isEqual(getIds(section1?.skills), getIds(section2?.skills)) &&
        isEqual(getIds(section1?.licenses), getIds(section2?.licenses)) &&
        isEqual(getIds(section1?.certifications), getIds(section2?.certifications))
}

export const calculateSectionsWithAutomaticBreaks = (baseSections: any, breaks: any) => {
    const firstSection = baseSections?.[0];
    const startDate = firstSection?.start;
    const endDate = baseSections?.[baseSections?.length - 1]?.end;
    const baseSectionData = omit(firstSection, ["start", "end", "type"]);

    const duration = moment(breaks?.[0]?.start || endDate).diff(moment(startDate), "seconds");

    let sections = [{
        ...baseSectionData,
        start: startDate,
        end: breaks?.[0]?.start || endDate,
        duration: renderDurationAsFormat(duration, "HH:mm"),
        type: "regular",
    }];

    if (breaks?.length === 0 && baseSections?.length > 1 && !baseSections.some(({ type }: { type: string }) => type === "break")) {
        sections = mergeSections(baseSections);
    }

    (breaks ?? []).forEach((generatedBreak: any, i: number) => {
        const nextGeneratedBreak = breaks[i + 1];
        sections.push(generatedBreak);

        if (nextGeneratedBreak) {
            const breakDuration = moment(nextGeneratedBreak.start).diff(moment(generatedBreak.end), "seconds");
            sections.push({
                ...baseSectionData,
                start: generatedBreak.end,
                end: nextGeneratedBreak.start,
                duration: renderDurationAsFormat(breakDuration, "HH:mm"),
                type: "regular",
            });
        }
    });

    if (breaks?.length > 0) {
        let remainingSections = (baseSections || [])?.slice(breaks.length).filter((item: any) => item?.type !== "break");

        if (remainingSections?.length > 0) {
            const remainingDuration = moment(remainingSections[0]?.end).diff(moment(breaks[breaks.length - 1]?.end), "seconds");
            remainingSections[0] = {
                ...remainingSections[0],
                start: breaks[breaks.length - 1]?.end,
                duration: renderDurationAsFormat(remainingDuration, "HH:mm"),
            };
            remainingSections = mergeSections(remainingSections);
            sections.push(...remainingSections);
        } else {
            const lastSectionDuration = moment(endDate).diff(moment(breaks[breaks.length - 1]?.end), "seconds");
            sections.push({
                ...baseSectionData,
                start: breaks[breaks.length - 1]?.end,
                end: endDate,
                duration: renderDurationAsFormat(lastSectionDuration, "HH:mm"),
                type: "regular",
            });
        }
    }

    return sections;
};

// Reconciles overlapping sections like layers painted onto a timeline: sections are
// applied in the order they were last edited (untouched/base sections keep their
// original relative order and are painted first), and each newly painted section wins
// its full range - clipping partially-overlapped sections at the boundary and dropping
// any section it fully covers - regardless of how many existing sections it overlaps.
export const calculateSections = (sections: any) => {
    const paintOrder = sections
        .map((section: any, index: number) => ({ section, index }))
        .sort((a: any, b: any) => {
            const aTime = a.section?.hasChanges?.time || 0;
            const bTime = b.section?.hasChanges?.time || 0;
            return aTime !== bTime ? aTime - bTime : a.index - b.index;
        });

    let timeline: any[] = [];

    paintOrder.forEach(({ section }: any) => {
        const start = moment(section.start).unix();
        const end = moment(section.end).unix();

        const remaining: any[] = [];
        timeline.forEach((existing: any) => {
            const existingStart = moment(existing.start).unix();
            const existingEnd = moment(existing.end).unix();

            if (existingEnd <= start || existingStart >= end) {
                remaining.push(existing);
                return;
            }

            if (existingStart < start) {
                remaining.push({
                    ...existing,
                    end: section.start,
                    duration: renderDurationAsFormat(start - existingStart, "HH:mm")
                });
            }

            if (existingEnd > end) {
                remaining.push({
                    ...existing,
                    start: section.end,
                    duration: renderDurationAsFormat(existingEnd - end, "HH:mm")
                });
            }
        });

        remaining.push(section);
        timeline = remaining;
    });

    timeline.sort((a: any, b: any) => moment(a.start).unix() - moment(b.start).unix());

    return mergeSections(timeline);
}

export const checkSectionsDuration = (sections: any, breakAmount: any) => {
    const minDuration = breakAmount || __MIN_SECTION_DURATION;

    const kept = sections.filter((section: any) => {
        return moment(section.end).diff(moment(section.start), "seconds") >= minDuration;
    });

    // Dropping a section leaves a hole in the middle of the shift (e.g. a break shorter
    // than the mandatory duration is removed, and what is left is two regular sections
    // with dead time between them). Close the hole by extending the preceding section up
    // to the next one, so the shift stays continuous and keeps its original end, then
    // merge the neighbours that have become identical back into a single section.
    const filled = kept.map((section: any, index: number) => {
        const next = kept[index + 1];
        if (!next || !moment(next.start).isAfter(moment(section.end))) {
            return section;
        }

        return {
            ...section,
            end: next.start,
            duration: renderDurationAsFormat(
                moment(next.start).diff(moment(section.start), "seconds"),
                "HH:mm"
            )
        };
    });

    return mergeSections(filled);
};

const checkBreaks = (sections: any, defaultSection: any) => {
    if (!sections.length) return sections;

    const firstSection = sections[0];
    const lastSection = sections[sections.length - 1];

    const createBreakSection = (start: any, end: any) => {
        const section = defaultSection;
        section.start = start;
        section.end = end;
        section.duration = renderDurationAsFormat(__MIN_SECTION_DURATION, "HH:mm");
        return section;
    };

    if (firstSection?.type === "break") {
        const newSectionAtTop = createBreakSection(moment(firstSection.start).subtract(__MIN_SECTION_DURATION, "seconds"), moment(firstSection.start));
        sections.unshift(newSectionAtTop);
    }

    if (lastSection?.type === "break") {
        const newSectionAtEnd = createBreakSection(moment(lastSection.end), moment(lastSection.end).add(__MIN_SECTION_DURATION, "seconds"));
        sections.push(newSectionAtEnd);
    }

    return sections;
};

export const recalculateSections = (sections: any, defaultSection: any, breakAmount: any, calcGroupBreaks: any, startDate: any) => {
    if (startDate) {
        if (startDate) {
            sections = sections?.map((section: any) => {
                // Check if start/end are already moments to avoid double-combining
                let start = moment.isMoment(section.start)
                    ? section.start
                    : (typeof section?.start === "number"
                        ? moment(startDate).add(section?.start, "seconds")
                        : combineDateAndTime(moment(startDate), moment(section?.start)));

                let end = moment.isMoment(section.end)
                    ? section.end
                    : (typeof section?.end === "number"
                        ? moment(startDate).add(section?.end, "seconds")
                        : combineDateAndTime(moment(startDate), moment(section?.end)));

                return { ...section, start, end };
            });
        }
    }
    sections.sort((a: any, b: any) => moment(a.start).unix() - moment(b.start).unix());
    sections = mergeSections(sections);
    sections = calculateSections(sections);

    if (calcGroupBreaks?.automatic?.status) {
        // Check if automatic breaks can be applied
        const canCalculateAutomaticBreaks = sections.find((item: any) => item?.canCalculateAutomaticBreaks);
        if (!!canCalculateAutomaticBreaks) {
            const breaks = getAutomaticBreaks(calcGroupBreaks, sections, defaultSection);
            sections = calculateSectionsWithAutomaticBreaks(sections, breaks);  // Apply automatic breaks
        }
    }
    sections = checkBreaks(sections, defaultSection);  // Validate breaks
    return checkSectionsDuration(sections, breakAmount);
}
